/**
 * The TDS Reco API client.
 *
 * It is a thin layer over `session.authed` from `@fiveox/shell` — that is
 * where the bearer, the single serialized refresh and the one place a session
 * ends already live (see `session.ts`). What this file adds is the two things
 * the shell cannot know about: **the org header** and **this app's error
 * envelope**.
 */

import { SERVICE_API } from './config'
import { SELECTED_ORG_KEY, setSelectedOrgId } from './orgs'
import { session } from './session'

/**
 * A refusal with the machine code the service sent.
 *
 * The code is the contract: a screen switches on `SEAT_LIMIT_EXCEEDED` to say
 * "5 of 5 seats in use" and on `NOT_ENTITLED` to render the paywall. Throwing a
 * bare `Error` here is what produces "something went wrong".
 */
export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the refusal is about entitlement rather than about this user. */
  get isPaywall(): boolean {
    return this.code === 'NOT_ENTITLED' || this.code === 'USAGE_LIMIT_EXCEEDED'
  }

  get entitlementReason(): string | undefined {
    const reason = this.details.entitlement_reason
    return typeof reason === 'string' ? reason : undefined
  }
}

// -- the selected org --------------------------------------------------------
//
// A user belongs to many orgs and switches between them, so the selected org
// is **not** derivable from the token: the client sends it and the service
// proves the user may act in it (FiveOX ADR-0029).

let currentOrgId: string | null =
  typeof localStorage === 'undefined' ? null : localStorage.getItem(SELECTED_ORG_KEY)

const orgListeners = new Set<() => void>()

export function setOrgId(orgId: string | null): void {
  if (orgId === currentOrgId) return
  currentOrgId = orgId
  setSelectedOrgId(orgId)
  for (const listener of orgListeners) listener()
}

export const getOrgId = () => currentOrgId

export function subscribeOrg(listener: () => void): () => void {
  orgListeners.add(listener)
  return () => orgListeners.delete(listener)
}

// -- the fetcher -------------------------------------------------------------

/**
 * **`X-Fiveox-Org`** — the header the whole estate speaks, under its new name
 * (APP_REPO_REBRAND_GUIDE §3, ADR-0013). Not `X-Org-Id`: ProGST invented that
 * name and no core service reads it, so a core caller sending the correct
 * header would have been refused. Corrected there 2026-08-25; inherited here
 * correct.
 *
 * **This app emits the new name only, and that is safe because of who reads
 * it:** the org header goes to `SERVICE_API` — our own service — and nowhere
 * else. Calls to the six core APIs go through `session.authed` without it. Our
 * service accepts both names, and the web bundle and the service ship from one
 * compose file, so there is no window where the emitter is ahead of its reader.
 */
const ORG_HEADER = 'X-Fiveox-Org'

export interface Envelope<T> {
  success: boolean
  data: T
  meta?: Record<string, unknown>
}

interface Options extends Omit<RequestInit, 'body'> {
  body?: unknown
  /** For routes that are not org-scoped. */
  withoutOrg?: boolean
  /** A step-up assertion from core-mfa, for an action that cannot be undone. */
  stepUpAssertion?: string
}

export async function api<T>(path: string, options: Options = {}): Promise<Envelope<T>> {
  // **A plain object, not a `Headers` instance — and this is not a style
  // choice.** `@fiveox/shell`'s `authed` merges with
  // `{ ...(init.headers ?? {}), authorization: ... }`, and spreading a
  // `Headers` yields `{}` because it has no enumerable own properties. Passing
  // one silently drops every header except the bearer the shell adds itself.
  //
  // That cost an afternoon: the org header (then `X-Fiveox-Org`) never left the browser, the
  // service refused with 400, the UI read it as `authority_unreachable` and
  // told the user the permission service was down. `content-type` was being
  // lost the same way, so every POST would have followed.
  //
  // Reported upstream — see docs/PLATFORM_INTEGRATION.md §9.3.
  const headers: Record<string, string> = {}
  for (const [key, value] of new Headers(options.headers).entries()) headers[key] = value
  if (options.body !== undefined) headers['content-type'] = 'application/json'
  if (!options.withoutOrg) {
    if (!currentOrgId) {
      throw new ApiError('VALIDATION_ERROR', 'No organisation is selected.', 400)
    }
    headers[ORG_HEADER] = currentOrgId
  }
  if (options.stepUpAssertion) headers['X-StepUp-Assertion'] = options.stepUpAssertion

  // `session.authed` owns the bearer, the 401 refresh-and-replay, and ending
  // the session when the refresh comes back spent.
  const res = await session.authed(`${SERVICE_API}${path}`, {
    ...options,
    headers,
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  })

  const body = (await res.json().catch(() => null)) as
    | (Envelope<T> & {
        error?: { code: string; message: string; details?: Record<string, unknown> }
      })
    | null

  if (!res.ok || !body?.success) {
    throw new ApiError(
      body?.error?.code ?? 'INTERNAL',
      body?.error?.message ?? 'Something went wrong.',
      res.status,
      body?.error?.details ?? {},
    )
  }
  return body
}

// -- the shapes the platform half serves -------------------------------------

export interface ClientRecord {
  id: string
  legal_name: string
  trade_name: string | null
  status: 'active' | 'archived'
  engagement_owner_user_id: string | null
  created_at: string
  /** Only present on a single read, for a caller holding `client.can_update`.
   *  Never in a list payload (spec FR-18). */
  pan?: string
}

export const clients = {
  list: (params: { search?: string; status?: string; limit?: number; offset?: number } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== '') query.set(key, String(value))
    }
    const suffix = query.toString() ? `?${query}` : ''
    return api<ClientRecord[]>(`/api/v1/clients${suffix}`)
  },
  get: (id: string) => api<ClientRecord>(`/api/v1/clients/${id}`),
  create: (body: { legal_name: string; pan: string; trade_name?: string }) =>
    api<ClientRecord>('/api/v1/clients', { method: 'POST', body }),
  archive: (id: string) =>
    api<ClientRecord>(`/api/v1/clients/${id}/archive`, { method: 'POST' }),
}

// Later phases add their clients here — uploads/runs (P3), reconciliation
// (P4), resolutions (P5), reports (P6) — so every call goes through `api()`
// and inherits the org header and the error envelope.
